/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import styled from '@emotion/styled';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { WorkspaceContext } from '/views/workspace/workspaceContext';
import { FacetDetail } from '/views/filterHub/facet/facetDetail';
import { FacetSearch } from '../facetSearch';
import { AutoSizer, InfiniteLoader, List } from 'react-virtualized';
import { useFacetsState } from 'hooks/useFacetsState';
import { selectDilDefinitionByName } from 'srs-middleware';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { facets } from 'widgets/endpoints';
import { Sizes } from 'styles/variables-slykit';
import { alignedMinimalButton, DividerFull, FlexSplitter } from 'styles/styledComponentUtils';
import { FacetHeader } from 'components/facets/facetHeader';
import { SORT_TYPES, SortSelect } from '../../../components/sortSelect';
import { ScrollBox } from 'components/scrollBox';
import { ExcludedFacetsDisplay, FACET_ITEM_HEIGHT, FacetDetailSkeleton } from '../../../components/facets/facetItem';
import { getReadOnlyOverride } from 'utilities/readOnlyOverrides';
import { EmptyFacetsMessage } from 'components/facets/emptyFacetsMessage';

const querySize = 50;

/**
 * Facet displays an infinite scrolling list of all of the facets.
 * This component doesn't keep all of the facets in memory
 *
 * @param {function} closePanel passed down from blueprint PanelStack component. Closes panel and reopens the FacetPreview component
 * @param {DILProperty} property indicates the specific property to render
 */
export const FacetPanel = ({ closePanel, property }) => {
	const [indexRange, setIndexRange] = useState(null);
	const [localData, setLocalData] = useState([]);
	const [filters, setFilters] = useState(null);
	const [sort, setSort] = useState(() => {
		const sortingTypeByProperty = getReadOnlyOverride('defaultFacetSortOrder');
		return SORT_TYPES?.[sortingTypeByProperty?.[property.name]]?.label ?? SORT_TYPES.NUMERIC.label;
	});
	const {
		state: {
			explorer: { selectedDefinitionName },
			transient: { activeResult, rootResult },
		},
	} = useContext(WorkspaceContext);
	const { updateFacets, getActiveFacets, getTextFilter, toggleExcludeFacet } = useFacetsState();
	const textFilter = getTextFilter();

	const selectedDefinition = useMemo(() => {
		return selectDilDefinitionByName(selectedDefinitionName);
	}, [selectedDefinitionName]);

	// clear the local data whenever the text filter changes
	useEffect(() => {
		setLocalData([]);
	}, [textFilter]);

	// memoize the parameters, so we don't keep fetching on rerender
	const parameters = useMemo(() => {
		if (!indexRange || !rootResult) return null;

		return {
			maxValues: 50,
			...indexRange,
			textFilter: textFilter,
			sort: sort === SORT_TYPES.NUMERIC.label ? SORT_TYPES.NUMERIC.value : SORT_TYPES.ALPHABETICAL.value,
		};
	}, [indexRange, textFilter, sort, rootResult]);

	// fetch the data, given the current parameters
	const { data, loading } = useAsyncCallback(async () => {
		const parametersWithParentId = { ...parameters, parentResultId: rootResult.resultId}
		if (rootResult && activeResult && property && selectedDefinition) {
			if (activeResult.count > 0) {
				const response = await facets(activeResult, selectedDefinitionName, {
					propertyList: [property.name],
					parameters: parametersWithParentId,
				});

				const filters = response[selectedDefinition.systemDefinitionId]?.[property.name];
				return { filters };
			}
		}
	}, [activeResult, property, rootResult, selectedDefinition, parameters]);

	// pull out the filters from the return value of the async callback
	// need to keep using the old filters until we new filters so the screen doesn't blink to blank during loading
	useEffect(() => {
		if (!loading) {
			// SRS keys are case insensitive which can cause some problems, transform key to lowercase
			if (data?.filters) {
				data.filters.entries.forEach((entry) => {
					entry.label = entry.key;
					entry.key = entry.key.toLowerCase();
				});
				setFilters(data.filters);
			}
		}
	}, [data, loading]);

	useEffect(() => {
		if (rootResult === null) {
			closePanel();
		}
	}, [rootResult, closePanel]);

	// we cache the data so we know what we have
	// TODO: This should be a cache, and not keep everything forever
	useEffect(() => {
		if (!filters) return;

		const formattedData = filters.entries;

		setLocalData((localData) => {
			const newLocalData = [...localData];
			let i = 0;
			for (; i < formattedData.length; i++) {
				newLocalData[indexRange.startIndex + i] = formattedData[i];
			}
			return newLocalData.slice(0, indexRange.startIndex + i);
		});
	}, [filters, indexRange]);

	// Only load 1 page of items at a time.
	const loadMoreRows = ({ startIndex, stopIndex }) => {
		setIndexRange({ startIndex: startIndex, endIndex: stopIndex });
	};

	// check the cache for the proper index
	const isRowLoaded = useCallback(
		({ index }) => {
			return localData[index] !== undefined;
		},
		[localData]
	);

	const FacetItem = ({ style, key, index }) => {
		if (!isRowLoaded({ index })) {
			return (
				<FacetDetailSkeleton key={key} style={style} css={facetItemStyle}>
					loading...
				</FacetDetailSkeleton>
			);
		}

		const { key: name, label, count, reference } = localData[index];

		return (
			<FacetDetail
				key={key}
				style={style}
				propertyName={property.name}
				facetName={label}
				facetKey={name}
				facetActiveValue={count}
				facetTotalValue={reference}
				css={facetItemStyle}
			/>
		);
	};

	const resetAllFacets = () => {
		updateFacets(property.name, []);
	};

	return (
		<Wrapper>
			<section>
				<FacetHeader
					title={property.name}
					loading={loading}
					onResetClick={resetAllFacets}
					onBackClick={closePanel}
					css={css`
						margin: ${Sizes.md} ${Sizes.lg} ${Sizes.sm};
					`}
				/>

				<DividerFull />

				<div css={optionsRow}>
					<SortSelect
						sortOptions={[SORT_TYPES.NUMERIC.label, SORT_TYPES.ALPHABETICAL.label]}
						selectedSortOption={sort}
						setSelectedSortOption={setSort}
						css={[alignedMinimalButton]}
					/>

					<FlexSplitter />

					<FacetSearch findIn={property.name} />
				</div>

				<DividerFull />
			</section>

			<ExcludedFacetsDisplay
				excludedFacets={getActiveFacets()}
				toggleFacet={toggleExcludeFacet}
				property={property.name}
			/>

			<ScrollBox>
				{filters?.fullSize != null && filters.fullSize === 0 ? (
					<EmptyFacetsMessage css={emptyMessageStyles} />
				) : (
					<InfiniteLoader
						loadMoreRows={loadMoreRows}
						isRowLoaded={isRowLoaded}
						rowCount={filters?.fullSize || 1}
						minimumBatchSize={querySize}
					>
						{({ onRowsRendered, registerChild }) => (
							<AutoSizer>
								{({ width, height }) => {
									return (
										<List
											ref={registerChild}
											width={width}
											height={height}
											onRowsRendered={onRowsRendered}
											rowHeight={FACET_ITEM_HEIGHT}
											rowCount={filters?.fullSize || 1}
											rowRenderer={FacetItem}
										/>
									);
								}}
							</AutoSizer>
						)}
					</InfiniteLoader>
				)}
			</ScrollBox>
		</Wrapper>
	);
};

const optionsRow = css`
	display: flex;
	align-items: center;
	margin: ${Sizes.xxsm} ${Sizes.lg};
`;

const Wrapper = styled.div`
	width: 100%;
	height: 100%;
	overflow: hidden;
	display: flex;
	flex-direction: column;
`;

const facetItemStyle = css`
	height: ${FACET_ITEM_HEIGHT}px;
`;

const emptyMessageStyles = css`
	margin: ${Sizes.sm} ${Sizes.lg};
`;
